/**
 * @file custom-view-types.ts
 * @description Types and interfaces for the custom views system.
 * This file defines the data structure used to represent custom views
 * and their components, allowing users to organize and filter their content.
 */

import type { IDateValue } from "../../../utils";

// #region VIEW COMPONENT INTERFACES

/**
 * Widget configuration for custom views
 */
type ViewWidgetSettings = {
    /** Display of upcoming meetings */
    showUpcomingMeetings: boolean;

    /** Display of general todo list */
    showTodo: boolean;

    /** Display of documents */
    showDocuments: boolean;

    /** Display of links */
    showLinks: boolean;

    /** Display of notes */
    showNotes: boolean;
};

enum ViewFilterMailStatus {
    userProcessed = "userProcessed",
    notUserProcessed = "notUserProcessed",
    focus = "focus",
    notFocus = "notFocus",
}

/**
 * Filters applied to a custom view
 */
type ViewFilter = {
    /** List of keywords used to filter content (e.g., "Mailvista", "Baba yaga") */
    keywords: string[];

    /** Start date to filter content (e.g., "From January 2024") */
    startDate: string | null;

    /** Start date to filter content (e.g., "From January 2024") */
    endDate: string | null;

    /** List of mails sender */
    identifiedSenders?: string[];

    /** List of mails recipient */
    identifiedRecipients?: string[];

    /** List of categories mails (e.g., "Important", "Rappel") */
    categories?: string[];

    /** Read status of emails ("read" or "unread") */
    readStatus?: string;

    /** Filter emails with or without attachments */
    hasAttachment?: boolean;

    /** List of status mails (e.g., 'userProcessed', 'notUserProcessed', 'focus', 'notFocus') */
    mailStatus?: string[];

    /** Sort Order Default value must be CustomViewSortOrder.DESC */
    sortOrder: CustomViewSortOrder;
};

// #endregion

// #region MAIN CUSTOM VIEW INTERFACE

enum CustomViewType {
    Custom = "Custom",
    Focus = "smart-label-focus",
    PourInfo = "smart-label-pour-info",
    Newsletter = "smart-label-newsletter",
    Suspect = "smart-label-suspect",
    Autre = "smart-label-autre",
    Traites = "smart-label-traites",
}

/**
 * Complete custom view
 * Allows the user to organize and access their content according to their criteria
 */
type CustomView = {
    /** Unique view identifier */
    id?: string;

    /** Owner user identifier */
    userId: string;

    /** View name (e.g., "Mailvista") */
    name: string;

    /** Icon identifier used for the view */
    icon: string;

    /** Type of Custom View : specific SmartLabel or Custom */
    viewType: CustomViewType;

    /** Filters applied to the view */
    filters: ViewFilter;

    /** Display mode (table or grid) */
    displayMode: CustomViewDisplayMode;

    /** View widget configuration */
    widgets: ViewWidgetSettings;

    // /** Content associated with the view */
    // content: ViewContent;

    /** View creation date */
    createdAt: IDateValue;

    /** View last modification date */
    updatedAt: IDateValue;

    /** Indicates if the view is a favorite */
    isFavorite: boolean;

    /** Order of the view in the list */
    displayOrder: number;

    /** Prompt used to gererate view parameters */
    userPrompt: string;

    /** Notes associated to view */
    noteContent: string;

    /** internetMessageIds contained in view */
    internetMessageIds: string[];

    /** Composition datée de la vue, même ordre que `internetMessageIds`. Le Record des mails ne
     *  contient que les mails chargés : il ne peut pas dater une vue entière. */
    mails: CustomViewMailEntry[];
};

/**
 * Uncomplete custom view
 * Only used in Custom View Creation Process
 */
type CustomViewInput = {
    /** Unique view identifier */
    id?: string;

    /** Owner user identifier */
    userId: string;
    /** View name (e.g., "Mailvista") */
    name: string;
    /** Icon identifier used for the view */
    icon: string;
    /** Type of Custom View : specific SmartLabel or Custom */
    viewType?: CustomViewType;

    /** Filters applied to the view */
    filters: ViewFilter;

    /** Display mode (table or grid) */
    displayMode: CustomViewDisplayMode;

    /** View widget configuration */
    widgets: ViewWidgetSettings;

    /** View creation date */
    createdAt?: IDateValue;
    /** View last modification date */
    updatedAt?: IDateValue;

    /** Indicates if the view is a favorite */
    isFavorite?: boolean;

    /** Order of the view in the list */
    displayOrder?: number;

    /** Prompt used to gererate view parameters */
    userPrompt: string;

    /** Notes associated to view */
    noteContent: string;

    /** internetMessageIds contained in view */
    internetMessageIds: string[];
};

// CustomViewPatchParams
// Only used to patch custom view in database

/**
 * Un mail dans la composition d'une CustomView : son identifiant et sa date d'envoi.
 * La date est la clé d'ordre, stockée et triée par l'API. `null` quand elle n'est pas connue —
 * le mail reste membre de la vue et va en fin de liste.
 */
type CustomViewMailEntry = {
    internetMessageId: string;
    sentDateTime: Date | null;
};

type CustomViewPatchParams = {
    /** Owner user identifier */
    userId?: string;

    /** View name (e.g., "Mailvista") */
    name?: string;

    /** Icon identifier used for the view */
    icon?: string;

    /** Filters applied to the view */
    filters?: ViewFilter;

    /** Display mode (table or grid) */
    displayMode?: CustomViewDisplayMode;

    /** View widget configuration */
    widgets?: ViewWidgetSettings;

    // /** Content associated with the view */
    // content: ViewContent;

    /** View creation date */
    createdAt?: IDateValue;

    /** View last modification date */
    updatedAt?: IDateValue;

    /** Indicates if the view is a favorite */
    isFavorite?: boolean;

    /** Order of the view in the list */
    displayOrder?: number;

    /** Notes associated to view */
    noteContent?: string;

    /** Composition datée de la vue : l'API stocke la date et porte l'ordre. */
    mails?: CustomViewMailEntry[];
};

enum CustomViewSortOrder {
    DESC = "DESC",
    ASC = "ASC",
}

type CustomViewDisplayMode = "TABLE" | "MOSAIC";

// #endregion

// #region EMPTY OBJECT FOR INITIALIZATION

/**
 * Empty custom view for initialization
 * Used as default value when creating a new view
 */
const emptyCustomView: CustomView = {
    id: "",
    userId: "",
    name: "",
    icon: "✨",
    viewType: CustomViewType.Custom,
    filters: {
        keywords: [],
        startDate: null,
        endDate: null,
        identifiedSenders: [],
        identifiedRecipients: [],
        sortOrder: CustomViewSortOrder.DESC,
    },
    displayMode: "TABLE",
    widgets: {
        showUpcomingMeetings: true,
        showTodo: true,
        showDocuments: true,
        showLinks: true,
        showNotes: true,
    },
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isFavorite: false,
    displayOrder: 99,
    userPrompt: "",
    noteContent: "",
    internetMessageIds: [],
    mails: [],
};

// #endregion

// #region SERVICE INTERFACES

// /**
//  * Interface for retrieving a user's views
//  */
// type GetViewsByUserIdParams = {
//   /** User identifier */
//   userId: string;
// }

// /**
//  * Interface for updating view widgets
//  */
// type UpdateViewWidgetsParams = {
//   /** ID of the view to update */
//   viewId: string;

//   /** New widget parameters */
//   widgets: ViewWidgetSettings;
// }

// /**
//  * Interface for updating view content
//  */
// type UpdateViewContentParams = {
//   /** ID of the view to update */
//   viewId: string;

//   /** New view content */
//   content: ViewContent;
// }

// /**
//  * Interface for updating view filters
//  */
// type UpdateViewFilterParams = {
//   /** ID of the view to update */
//   viewId: string;

//   /** New filters to apply */
//   filter: ViewFilter;
// }

// #endregion

type StartupMails = {
    focusMailsInternetMessageIds: string[];
    favoriteCustomViewsMailsInternetMessageIds: string[];
    otherViewsMailsInternetMessageIds: string[];
};

/**
 * Entrée interne SDK pour un mail dans un SmartLabel ou une CustomView.
 * SmartLabels : date et score toujours renseignés (fournis par l'API).
 * CustomViews : date et score sont undefined (contrat CustomView inchangé).
 */
type SmartLabelEntry = {
    id: string; // = internetMessageId
    date?: Date; // = sentDateTime converti (SmartLabels uniquement)
    score?: number; // score IA (SmartLabels uniquement)
    manualFocusDate?: string | null; // RG2 — date de focus manuel (Focus uniquement)
    sentDateTimeMailvista?: string | null; // Clé tri Focus groupe 1 — non null si mail système Mailvista
};

// Exports
export type {
    ViewFilter,
    CustomView,
    ViewWidgetSettings,
    CustomViewInput,
    CustomViewMailEntry,
    CustomViewPatchParams,
    StartupMails,
    SmartLabelEntry,
    // GetViewsByUserIdParams,
    // UpdateViewWidgetsParams,
    // UpdateViewContentParams,
    // UpdateViewFilterParams
};

export {
    emptyCustomView,
    CustomViewSortOrder,
    CustomViewType,
    ViewFilterMailStatus,
};

export type { CustomViewDisplayMode };

/**
 * Structure and organization:
 * - Complete interfaces with "I" prefix to respect convention
 * - Detailed documentation with JSDoc comments for each interface and property
 * - Organization in thematic regions to facilitate navigation
 * - Specific types to improve type safety (ViewDisplayMode)
 * - emptyCustomView object with appropriate default values
 * - Service interfaces for common API operations
 * - Exports organized at the end of the file to facilitate importing
 */
